import { DatePipe } from '@angular/common';
import { CONSTANTS } from '@pk/powerkioskutils';

import _ from 'lodash';
import moment from 'moment-timezone';

import { ServiceLocator } from '../../shared/service-locator.service';
import { AccountManager } from './account-manager';
import { Agent } from './agent';
import { Company } from './company';
import { Menu } from './menu';
import { Role } from './role';
import { Supplier } from './supplier';
import { UserAPIKey } from './user-api-key';
import { UserFeatureFlag } from './user-feature-flag';
import { UserGridConfig } from './user-grid-config';
import { UserNotification } from './user-notification';
import { UserRfqSetting } from './user-rfq-setting';

export class User {
	public userId: string;
	public agentId: string;
	public username: string;
	public fname: string;
	public middle: string;
	public lname: string;
	public roleId: string;
	public lastLogin: Date;
	public phone: string;
	public phone2: string;
	public fax: string;
	public mobile: string;
	public phoneCountryId: number;
	public phone2CountryId: number;
	public faxCountryId: number;
	public mobileCountryId: number;
	public supervisorId: string;
	public supervisor2Id: string;
	public subscribeToErrorNotifications: boolean;
	public password: string;
	public confirmpassword: string;
	public customerId: string;
	public supplierId: string;
	public isActive: boolean;
	public addDate: Date;
	public stripeCustomerId: string;
	public ipAddress: string;
	public isPasswordExpired: boolean;
	public showTermsOfService: boolean;
	public fullName: string;
	public defaultUserGridConfigId: number;
	public defaultElectricityUnit: string;
	public defaultGasUnit: string;
	public isLocked: boolean;
	public showCompliance: boolean;
	public signature: string;
	public defaultRateSort: string;
	public showIdDocument: boolean;
	public companyId: string;
	public isSaas: boolean;
	public isAdmin: boolean;
	public isOwner: boolean;
	public isFinance: boolean;
	public isOperations: boolean;
	public idDocumentAttachmentId: string;
	public isCommissionUser: boolean;
	public isPrimary: boolean;
	public dob: Date;
	public sendPeakDemandNotification: boolean;
	public showAdvisorAcademyTour: boolean;
	public isSalesUser: boolean;
	public email: string;
	public billReaderUserId: string;

	public helpScoutSignature: string;
	public supervisor: User;
	public role: Role;
	public agent: Agent;
	public supplier: Supplier;
	public reportAgents: Agent[];
	public supervisors: User[];
	public rfqSettings: UserRfqSetting[];
	public apiKeys: UserAPIKey[];
	public gridConfigs: UserGridConfig[];
	public defaultUserGridConfig: UserGridConfig;
	public company: Company;
	public menus: Menu[];
	public userNotifications: UserNotification[];
	public accountManager: AccountManager;
	public featureFlags: UserFeatureFlag[];

	get accountManagerId(): number {
		return this.accountManager?.id;
	}

	constructor(user?: Subset<User>) {
		if (typeof user === 'string') {
			Object.assign(this, JSON.parse(user));
		} else {
			Object.assign(this, user);
		}

		this.role = this.role ? new Role(this.role) : this.role;
		this.agent = this.agent ? new Agent(this.agent) : this.agent;
		this.lastLogin = this.lastLogin ? new Date(moment(this.lastLogin).format('MMM DD, YYYY hh:mm:ss a')) : this.lastLogin;
		this.addDate = this.addDate ? new Date(moment(this.addDate).format('MMM DD, YYYY hh:mm:ss a')) : this.addDate;
		this.reportAgents = this.reportAgents ? this.reportAgents.map(a => new Agent(a)) : this.reportAgents;
		this.gridConfigs = this.gridConfigs
			? _.orderBy(
					this.gridConfigs.map(p => new UserGridConfig(p)),
					u => u.sortOrder,
					['asc'],
				)
			: this.gridConfigs;
		this.accountManager = this.accountManager ? new AccountManager(this.accountManager) : this.accountManager;
		this.featureFlags = this.featureFlags ? this.featureFlags.map(f => new UserFeatureFlag(f)) : this.featureFlags;
		this.company = this.company ? new Company(this.company) : this.company;
	}

	public isRole(role: string): boolean {
		return this.role && this.role.name.toLowerCase() === role.toLowerCase();
	}

	public hasAtLeastRoleLevel(level: number): boolean {
		return this.role && this.role.level > level;
	}

	public hasPermission(permission: string): boolean {
		return this.role && this.role.permissions && this.role.permissions.map(p => p.name).includes(permission);
	}

	public getContactFullName(): string {
		const contactMiddle = this.middle && this.middle.trim() ? this.middle.trim() + ' ' : '';
		return `${this.fname} ${contactMiddle}${this.lname}`;
	}

	public getFullNameLastFirst(): string {
		return `${this.lname}, ${this.fname} ${this.middle && this.middle.trim() ? ' ' + this.middle.trim() : ''}`;
	}

	public getLabel(): string {
		return `${this.fname} ${this.lname} [${this.email}]${this.isActive ? '' : ' [Inactive]'}`;
	}

	public get calendarOrganizer() {
		return `${this.labelShort}|${this.email}`;
	}

	public get labelShort() {
		return `${this.fname} ${this.lname}`;
	}

	public canImpersonate(): boolean {
		return this.isAdmin || this.isOwner;
	}

	public getDisplayLastLogin(): string {
		const datePipe = ServiceLocator.injector.get(DatePipe);
		if (this.lastLogin) {
			return datePipe.transform(this.lastLogin, 'MMM dd, yyyy hh:mm a') + ' CT';
		}

		return 'Never';
	}

	public getUserEditLink(): string[] {
		if (this.supplierId) {
			return ['/cPanel/users/edit/supplier', this.supplierId, this.userId];
		}

		if (this.agentId) {
			return ['/cPanel/users/edit/agent', this.agentId, this.userId];
		}

		const roleType = this.role.name.toLowerCase();

		return [`/cPanel/users/edit/${roleType}`, this.userId, this.userId];
	}

	public isTrupowur(): boolean {
		return this.agentId === CONSTANTS.agents.trupowur;
	}

	get showPricingAnalytics(): boolean {
		return this.menus?.some(m => m.menuId === CONSTANTS.menus.pricingAnalytics);
	}

	get hasPricingAnalyticsMatrix(): boolean {
		return !this.supplier || this.supplier.licensePlans?.some(l => CONSTANTS.licensePlans.pricingAnalyticsMatrix.includes(l.id));
	}

	get hasPricingAnalyticsCustom(): boolean {
		return !this.supplier || this.supplier.licensePlans?.some(l => CONSTANTS.licensePlans.pricingAnalyticsCustom.includes(l.id));
	}

	get hasPricingAnalyticsResidential(): boolean {
		return !this.supplier || this.supplier.licensePlans?.some(l => CONSTANTS.licensePlans.pricingAnalyticsResidential.includes(l.id));
	}

	get hasTurnKey(): boolean {
		return (
			!this.agent ||
			this.agent.licensePlans?.some(l => CONSTANTS.licensePlans.oldTurnKeyIds.concat(CONSTANTS.licensePlans.newTurnKeyIds).includes(l.id))
		);
	}

	get showAdvisorAcademy(): boolean {
		return this.menus?.some(m => m.menuId === CONSTANTS.menus.advisorAcademy);
	}

	get showReports(): boolean {
		return (!this.isSaas || this.company.showReports) && this.menus?.some(m => m.menuId === CONSTANTS.menus.reports);
	}

	get showNewsResources(): boolean {
		return this.menus?.some(m => m.menuId === CONSTANTS.menus.newsAndResources);
	}

	get showReferralExchange(): boolean {
		return this.menus?.some(m => m.menuId === CONSTANTS.menus.referralExchange);
	}

	get hasMatrixPricing(): boolean {
		return !this.isSaas || this.company.hasMatrixPricing;
	}

	get allowCommercialMatrixQuote(): boolean {
		return !this.isSaas || this.company.allowCommercialMatrixQuote;
	}

	get allowResidentialMatrixQuote(): boolean {
		return this.isSaas && this.company.allowResidentialMatrixQuote;
	}

	get residentialQuoteRequiresPOA(): boolean {
		return this.isSaas && this.company.residentialQuoteRequiresPOA;
	}

	get isDecoupled(): boolean {
		return !this.isSaas || this.company.isDecoupled;
	}

	get allowInstantQuote(): boolean {
		return !this.isSaas || this.company.allowInstantQuote;
	}

	get showCustomerPii(): boolean {
		return !this.isSaas || this.company.showCustomerPii;
	}

	get hasCustomPricing(): boolean {
		return !this.isSaas || this.company.hasCustomPricing;
	}

	get hasBufferMargin(): boolean {
		return !this.isSaas || this.company.hasBufferMargin;
	}

	get hasMarketAnalytics(): boolean {
		return !this.isSaas || this.company.hasMarketAnalytics;
	}

	get hasLayeredPurchasing(): boolean {
		return !this.isSaas || this.company.hasLayeredPurchasing;
	}

	get hasAccountUsage(): boolean {
		return !this.isSaas || this.company.hasAccountUsage;
	}

	get hasCommission(): boolean {
		return !this.isSaas || this.company.hasCommission;
	}

	get showResidentialSavings(): boolean {
		return !this.isSaas || this.company.showResidentialSavings;
	}

	get showResidentialBestPlan(): boolean {
		return !this.isSaas || this.company.showResidentialBestPlan;
	}

	get showResidentialQuoteLinkCustomizeTextBox(): boolean {
		return !this.isSaas || this.company.showResidentialQuoteLinkCustomizeTextBox;
	}

	get isNrg(): boolean {
		return this.companyId === CONSTANTS.companies.nrgEnergy;
	}
}
